import { baseLocale, type Locale } from '$lib/paraglide/runtime';
import type { WikiCategory } from './frontmatter';
import { wikiHref, type WikiIndex } from './wiki-index';

/**
 * Client-side search wiki — czysta logika bez globów Vite (testowalna w
 * projekcie `logic`). Indeks budowany build-time z WikiIndex, wyszukiwanie
 * działa w przeglądarce na stronach statycznych bez żadnego backendu.
 */

export interface WikiSearchRecord {
	pageId: string;
	title: string;
	description: string;
	category: WikiCategory;
	href: string;
	/** True gdy locale nie ma tłumaczenia — link prowadzi do treści EN. */
	fallback: boolean;
}

/**
 * Rekordy wyszukiwania dla języka — ta sama unia co sidebar (wersja natywna
 * albo wpis fallback do treści EN), żeby search i nawigacja pokazywały
 * identyczny zbiór stron.
 */
export function buildSearchRecords(index: WikiIndex, locale: Locale): WikiSearchRecord[] {
	const records: WikiSearchRecord[] = [];
	for (const [pageId, translations] of index.byPageId) {
		const page = translations[locale] ?? translations[baseLocale];
		if (page === undefined) continue;
		records.push({
			pageId,
			title: page.title,
			description: page.description,
			category: page.category,
			href: wikiHref(locale, page.slug),
			fallback: locale !== baseLocale && translations[locale] === undefined
		});
	}
	return records;
}

/**
 * Normalizacja do matchowania: lowercase + usunięcie diakrytyków (NFD zdejmuje
 * ogonki i kreski; `ł` nie dekomponuje się w NFD, stąd jawna zamiana), żeby
 * "slad" znajdował "ślad", a "lozko" — "łóżko".
 */
export function normalizeForSearch(text: string): string {
	return text.toLowerCase().replaceAll('ł', 'l').normalize('NFD').replace(/[̀-ͯ]/g, '');
}

const TITLE_PREFIX_SCORE = 4;
const TITLE_MATCH_SCORE = 2;
const DESCRIPTION_MATCH_SCORE = 1;

/**
 * Wyszukiwanie: każdy token zapytania musi wystąpić w tytule albo opisie
 * (AND), wynik sortowany malejąco po sumie trafień — prefiks tytułu waży
 * najwięcej, potem tytuł, potem opis. Puste zapytanie → brak wyników.
 */
export function searchWiki(
	records: WikiSearchRecord[],
	query: string,
	limit = 8
): WikiSearchRecord[] {
	const tokens = normalizeForSearch(query).split(/\s+/).filter(Boolean);
	if (tokens.length === 0) return [];

	const scored: { record: WikiSearchRecord; score: number }[] = [];
	for (const record of records) {
		const title = normalizeForSearch(record.title);
		const description = normalizeForSearch(record.description);
		let score = 0;
		for (const token of tokens) {
			if (title.startsWith(token)) {
				score += TITLE_PREFIX_SCORE;
			} else if (title.includes(token)) {
				score += TITLE_MATCH_SCORE;
			} else if (description.includes(token)) {
				score += DESCRIPTION_MATCH_SCORE;
			} else {
				score = 0;
				break;
			}
		}
		if (score > 0) scored.push({ record, score });
	}

	scored.sort((a, b) => b.score - a.score || a.record.title.localeCompare(b.record.title));
	return scored.slice(0, limit).map(({ record }) => record);
}
